import React from 'react';
import Section from './Section';
import { teamData } from '../data/team';

const InstagramIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const LinkIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const accentColors = [
  { bar: 'bg-gradient-to-r from-primary to-secondary', ring: 'ring-primary/20' },
  { bar: 'bg-gradient-to-r from-violet-400 to-purple-300', ring: 'ring-violet-200' },
  { bar: 'bg-gradient-to-r from-sky-400 to-background', ring: 'ring-sky-200' },
];

const Team = () => {
  return (
    <Section id="equipo" className="bg-gradient-to-b from-[#fdf6ff] to-white">

      {/* Header */}
      <div className="text-center mb-16">
        <span className="badge bg-secondary/40 text-text mb-4">Conoce al equipo</span>
        <h2 className="section-title mb-4">Nuestro Equipo</h2>
        <p className="section-subtitle">
          Profesionales especializadas comprometidas con el desarrollo integral de cada niño
        </p>
      </div>

      {/* Team cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {teamData.map((member, index) => {
          const accent = accentColors[index % accentColors.length];
          return (
            <div
              key={member.id}
              className="bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group flex flex-col"
            >
              {/* Accent top bar */}
              <div className={`h-2 ${accent.bar}`} />

              <div className="p-7 flex flex-col flex-1">
                {/* Photo */}
                <div className="flex justify-center mb-5">
                  <div className={`relative w-28 h-28 rounded-full ring-4 ${accent.ring} overflow-hidden shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="text-center space-y-1 mb-4">
                  <h3 className="font-fredoka text-2xl text-text">{member.name}</h3>
                  <p className="text-primary font-semibold text-sm">{member.role}</p>
                  <span className="inline-block bg-secondary/30 text-text/70 px-3 py-1 rounded-full text-xs font-semibold">
                    {member.specialty}
                  </span>
                </div>

                {/* Bio */}
                <p className="text-text/65 text-sm leading-relaxed text-center font-nunito mb-5">
                  {member.bio}
                </p>

                {/* Social links */}
                {member.socialMedia && (
                  <div className="flex justify-center flex-wrap gap-2 mb-5">
                    {member.socialMedia.instagram && (
                      <a
                        href={member.socialMedia.instagram.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 bg-pink-50 text-pink-600 hover:bg-pink-100 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <InstagramIcon />
                        {member.socialMedia.instagram.handle}
                      </a>
                    )}
                    {member.socialMedia.website && (
                      <a
                        href={member.socialMedia.website.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 bg-violet-50 text-violet-600 hover:bg-violet-100 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <LinkIcon />
                        Web
                      </a>
                    )}
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
                  className="mt-auto flex items-center justify-center gap-1 w-full py-2.5 rounded-2xl border-2 border-primary/20 text-primary text-sm font-semibold hover:bg-primary hover:text-white hover:border-primary transition-all duration-200 cursor-pointer"
                >
                  <CalendarIcon />
                  Agendar consulta
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="mt-16 bg-gradient-to-r from-primary/10 to-secondary/20 rounded-3xl p-10 text-center max-w-2xl mx-auto">
        <h3 className="font-fredoka text-3xl text-text mb-3">¿Necesitas orientación?</h3>
        <p className="text-text/65 mb-6 font-nunito">
          Nuestro equipo está aquí para ayudarte a encontrar la mejor opción para tu hijo
        </p>
        <button
          onClick={() => window.open('https://walink.co/eea878', '_blank')}
          className="btn-primary"
        >
          Escríbenos
        </button>
      </div>
    </Section>
  );
};

export default Team;
